import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  FileText,
  BedDouble,
  MapPin,
  Loader2,
  AlertCircle,
  Check,
  Edit,
  Building2,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { PhotoGallery } from "@/components/deal/PhotoGallery";
import { NotesLog } from "@/components/deal/NotesLog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/toast";

import { computeTotalRooms } from "@/lib/deals/normalizers";
import { setCompleted } from "@/lib/uwProgress";

import { CurrencyCode, Deal } from "@/types/deal";

import {
  FACILITY_OPTIONS,
  normalizeFacilities as normalizeFacilityIds,
  facilitiesToBooleans,
  booleansToFacilities,
  FacilityId,
  normalizeFacilities,
  facilityLabel,
} from "../../lib/facilities";
import RoomMixModal from "@/modules/underwriting/RoomMixModal";

import { patchProjectKpis } from "@/lib/datasource";

import { propertyTypes } from "@/constants/facities";
import { currencies } from "@/helpers/currencies";
import { getProjecById } from "@/lib/datasource/supabase";
import { getAllCountries } from "@/lib/payroll/templateService";
import { PayrollCountry } from "@/types/payrollTemplate";
import { totalRooms } from "@/lib/rooms";
import FacilitiesModal from "@/components/FacilitiesModal";
import { ICurrency } from "@/types";

interface PropertyDetailsFormNewProps {
  dealId: string;
  onSaved?: () => void;
}

export default function PropertyDetailsFormNew({
  dealId,
  onSaved,
}: PropertyDetailsFormNewProps) {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [deal, setDeal] = useState<Deal | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saveState, setSaveState] = useState<
    "idle" | "saving" | "success" | "error"
  >("idle");
  const [saveError, setSaveError] = useState<string>("");
  const [roomMixOpen, setRoomMixOpen] = useState(false);
  const [facilitiesOpen, setFacilitiesOpen] = useState(false);
  const [refreshToken, setRefreshToken] = useState<string>("");

  const [name, setName] = useState("");
  const [location, setLocation] = useState("");
  const [address, setAddress] = useState("");
  const [propertyType, setPropertyType] = useState<string>("Boutique");
  const [stars, setStars] = useState<number>(4);
  const [gfaSqm, setGfaSqm] = useState<number>(0);
  const [countries, setCountries] = useState<PayrollCountry[]>([]);
  const [purchasePrice, setPurchasePrice] = useState<number>(0);
  const [currency, setCurrency] = useState<ICurrency>("EUR");
  const [photoUrl, setPhotoUrl] = useState<string>("");
  const [photoUrlError, setPhotoUrlError] = useState<string>("");

  const [investmentThesis, setInvestmentThesis] = useState<string>("");
  const [photos, setPhotos] = useState<any[]>([]);
  const [notes, setNotes] = useState<any[]>([]);

  useEffect(() => {
    async function loadPropertyDetails() {
      try {
        const project = await getProjecById(dealId);

        if (!project) {
          toast.error("Project not found");
          navigate("/underwriting");
          return;
        }

        const kpis = project.kpis || {};
        const property = kpis.property || {};

        const loadedName =
          kpis.dealName || property.name || project.name || "Untitled Project";
        const loadedLocation = kpis.location || property.location || "";
        const loadedAddress = kpis.address || property.address || "";
        const loadedPropertyType =
          kpis.propertyType || property.type || "Boutique";
        const loadedStars = kpis.starRating || property.starRating || 4;
        const loadedPurchasePrice = (kpis.purchase && kpis.purchase.price) ?? 0;
        const loadedCurrency =
          kpis.currency || project.currency || ("EUR" as CurrencyCode);
        const loadedPhotoUrl = kpis.photoUrl || property.photoUrl || "";
        const loadedGfaSqm = kpis.gfaSqm || property.buildingGfaSqm || 0;
        const loadedInvestmentThesis = kpis.investmentThesis || "";
        const loadedPhotos = kpis.photos || [];
        const loadedNotes = kpis.notes || [];

        setName(loadedName);
        setLocation(loadedLocation);
        setAddress(loadedAddress);
        setPropertyType(loadedPropertyType);
        setStars(loadedStars);
        setPurchasePrice(loadedPurchasePrice);
        setCurrency(loadedCurrency);
        setPhotoUrl(loadedPhotoUrl);
        setGfaSqm(loadedGfaSqm);
        setInvestmentThesis(loadedInvestmentThesis);
        setPhotos(loadedPhotos);
        setNotes(loadedNotes);

        const constructedDeal: Deal = {
          id: project.id,
          name: loadedName,
          location: loadedLocation,
          address: loadedAddress,
          propertyType: loadedPropertyType,
          stars: loadedStars as Deal["stars"],
          starRating: loadedStars,
          facilities: kpis.facilities || [],
          rooms: (kpis.roomMix as any) || [],
          gfaSqm: loadedGfaSqm,
          currency: loadedCurrency,
          photoUrl: loadedPhotoUrl,
          purchasePrice: loadedPurchasePrice,
          amenities: kpis.amenities || {},
          normalizedFacilities: kpis.normalizedFacilities || [],
          fnbRevenue: kpis.fnbRevenue,
          otherRevenue: kpis.otherRevenue,
          budget: kpis.budget,
          createdAt: project.created_at,
          updatedAt: project.updated_at,
          normalizedRooms: [],
          roomTypes: [],
          kpis: kpis,
        };

        if (kpis.roomMix) {
          if (
            typeof kpis.roomMix === "object" &&
            "types" in kpis.roomMix &&
            Array.isArray(kpis.roomMix.types)
          ) {
            constructedDeal.normalizedRooms = kpis.roomMix.types.map(
              (rm: any) => ({
                id: rm.id || crypto.randomUUID(),
                name: rm.name,
                count: rm.rooms,
                sqm: rm.sqm,
                adrBase: rm.adrBase,
              }),
            );
            constructedDeal.roomTypes = kpis.roomMix.types.map((rm: any) => ({
              id: rm.id || crypto.randomUUID(),
              name: rm.name,
              rooms: rm.rooms,
              adrWeight: rm.adrWeight || 100,
            }));
          } else if (Array.isArray(kpis.roomMix) && kpis.roomMix.length > 0) {
            constructedDeal.normalizedRooms = kpis.roomMix.map((rm: any) => ({
              id: rm.id || crypto.randomUUID(),
              name: rm.type || rm.name,
              count: rm.count || rm.rooms,
              sqm: rm.sqm,
              adrBase: rm.adrBase,
            }));
            constructedDeal.roomTypes = kpis.roomMix.map((rm: any) => ({
              id: rm.id || crypto.randomUUID(),
              name: rm.type || rm.name,
              rooms: rm.count || rm.rooms,
              adrWeight: rm.adrWeight || 100,
            }));
          }
        }

        setDeal(constructedDeal);
      } catch (error) {
        console.error("Failed to load project:", error);
        toast.error("Failed to load project data.");
        navigate("/underwriting");
      }
    }

    loadPropertyDetails();
  }, [dealId, navigate, refreshToken]);

  useEffect(() => {
    async function fetchCountries() {
      try {
        const data = await getAllCountries();
        setCountries(data);
      } catch (err) {
        console.error("Failed to fetch countries", err);
      }
    }
    fetchCountries();
  }, []);

  const validatePhotoUrl = (url: string) => {
    if (!url.trim()) {
      setPhotoUrlError("");
      return true;
    }
    const urlPattern = /^https?:\/\/.+\.(jpg|jpeg|png|webp)(\?.*)?$/i;
    if (!urlPattern.test(url)) {
      setPhotoUrlError("Please enter a valid image URL (JPG, PNG, or WebP)");
      return false;
    }
    setPhotoUrlError("");
    return true;
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!name.trim()) newErrors.name = "Deal name is required";
    if (!location.trim()) newErrors.location = "Location is required";
    if (!propertyType) newErrors.propertyType = "Property type is required";
    if (stars < 1 || stars > 5)
      newErrors.stars = "Star rating must be between 1 and 5";
    if (gfaSqm < 0) newErrors.gfaSqm = "Floor area cannot be negative";
    if (purchasePrice < 0)
      newErrors.purchasePrice = "Purchase price cannot be negative";
    if (!currency) newErrors.currency = "Currency is required";
    if (photoUrl && photoUrlError) newErrors.photoUrl = photoUrlError;
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getCanonicalIds = useCallback((): string[] => {
    if (!deal) return [];

    if (
      Array.isArray(deal.normalizedFacilities) &&
      deal.normalizedFacilities.length > 0
    ) {
      return deal.normalizedFacilities;
    }

    if (Array.isArray(deal.facilities) && deal.facilities.length > 0) {
      const firstEl = deal.facilities[0];
      if (FACILITY_OPTIONS.some((f) => f.id === firstEl)) {
        return deal.facilities;
      }
      return normalizeFacilityIds(deal.facilities);
    }

    if (deal.amenities && Object.keys(deal.amenities).length > 0) {
      return booleansToFacilities(deal.amenities as any);
    }

    return [];
  }, [deal]);

  const handleSave = async () => {
    if (!deal || !validateForm() || saveState === "saving") return;

    setSaveState("saving");

    try {
      const numGfaSqm = Number(gfaSqm) || 0;
      const numStars = Number(stars) || 0;
      const strPropertyType = propertyType as Deal["propertyType"];

      const canonicalIds = getCanonicalIds() as string[];

      const facilitiesArray = canonicalIds
        .map((id) => FACILITY_OPTIONS.find((o) => o.id === id)?.label)
        .filter(Boolean) as string[];

      const amenitiesObject = facilitiesToBooleans(
        canonicalIds as FacilityId[],
      );

      const normalizedRooms = deal.normalizedRooms || [];
      const totalRoomsCount = computeTotalRooms(normalizedRooms);

      const cleanName = name.trim();

      const updatedDeal: Deal = {
        ...deal,
        name: cleanName,
        location: location.trim(),
        address: address.trim(),
        propertyType: strPropertyType,
        stars: numStars as Deal["stars"],
        gfaSqm: numGfaSqm,
        purchasePrice,
        currency,
        photoUrl: photoUrl.trim() || undefined,
        budget: deal.budget
          ? { ...deal.budget, netPurchasePrice: purchasePrice }
          : undefined,
        facilities: facilitiesArray,
        normalizedRooms: normalizedRooms,
        rooms: normalizedRooms.map((r) => ({
          id: r.id,
          type: r.name,
          count: r.count,
          sqm: r.sqm,
          adrBase: r.adrBase,
        })),
        roomTypes: normalizedRooms.map((r) => ({
          id: r.id,
          name: r.name,
          rooms: r.count,
          adrWeight: 100,
        })),
        amenities: amenitiesObject,
        normalizedFacilities: canonicalIds as any,
        updatedAt: new Date().toISOString(),

        kpis: {
          ...deal.kpis,
          dealName: cleanName,
          ...(deal.kpis?.name && { name: undefined }),
          dealLocation: location.trim(),
          location: location.trim(),
          address: address.trim(),
          propertyType: strPropertyType,
          starRating: numStars,
          currency: currency,
          photoUrl: photoUrl,
          gfaSqm: numGfaSqm,
          facilities: facilitiesArray,
          amenities: amenitiesObject,
          normalizedFacilities: canonicalIds,
          purchase: { price: Number(purchasePrice) || 0 },
          investmentThesis: investmentThesis,
          photos: photos,
          notes: notes,
          property: {
            ...(deal.kpis?.property || {}),
            facilities: facilitiesArray,
          },
          ...(normalizedRooms.length > 0 && {
            rooms: { total: totalRoomsCount },
            roomMix: {
              types: normalizedRooms.map((r) => ({
                id: r.id,
                name: r.name,
                rooms: r.count,
                sqm: r.sqm,
                adrBase: r.adrBase,
                adrWeight: 100,
              })),
              total: totalRoomsCount,
            },
            roomsTotal: totalRoomsCount,
          }),
        },
      };

      setDeal(updatedDeal);

      try {
        await patchProjectKpis(dealId, updatedDeal.kpis, { name: cleanName });

        console.log("[PropertyDetails] Saved with name:", cleanName);
      } catch (e) {
        console.warn("[PropertyDetails] Save failed:", e);
        throw new Error("Failed to save data to server.");
      }

      setCompleted(dealId, "propertyDetails", true);
      setSaveState("success");
      toast.success("Property details saved successfully");
      setRefreshToken(crypto.randomUUID());
      onSaved?.();

      setTimeout(() => setSaveState("idle"), 2000);
    } catch (error: any) {
      setSaveState("error");
      setSaveError(error.message || "Failed to save changes");
      toast.error(error.message || "Failed to save property details");

      setTimeout(() => {
        setSaveState("idle");
        setSaveError("");
      }, 3000);
    }
  };

  const handleCancel = () => {
    if (!deal) return;
    setName(deal.name);
    setLocation(deal.location);
    setAddress(deal.address);
    setPropertyType(deal.propertyType);
    setStars(deal.stars);
    setGfaSqm(deal.gfaSqm);
    setPurchasePrice(deal.purchasePrice);
    setCurrency(deal.currency);
    setPhotoUrl(deal.photoUrl || "");
    setInvestmentThesis(deal.kpis?.investmentThesis || "");
    setPhotos(deal.kpis?.photos || []);
    setNotes(deal.kpis?.notes || []);
    setErrors({});
  };

  const handleRoomMixSaved = () => {
    setRoomMixOpen(false);
    setRefreshToken(crypto.randomUUID());
    toast.success("Room mix updated");
    onSaved?.();
  };

  const handleFacilitiesArraySaved = async (newFacilityIds: string[]) => {
    if (!deal) return;

    const facilitiesLabels = newFacilityIds
      .map((id) => FACILITY_OPTIONS.find((o) => o.id === id)?.label)
      .filter(Boolean) as string[];

    const newAmenities = facilitiesToBooleans(newFacilityIds);

    const updatedDeal: Deal = {
      ...deal,
      amenities: newAmenities,
      facilities: facilitiesLabels,
      normalizedFacilities: newFacilityIds as any,
      updatedAt: new Date().toISOString(),
      kpis: {
        ...deal.kpis,
        amenities: newAmenities,
        facilities: facilitiesLabels,
        normalizedFacilities: newFacilityIds,
        property: {
          ...deal.kpis?.property,
          facilities: facilitiesLabels,
        },
      },
    };

    setDeal(updatedDeal);

    try {
      const kpiPatch = {
        amenities: newAmenities,
        facilities: facilitiesLabels,
        normalizedFacilities: newFacilityIds,
        property: {
          ...deal.kpis?.property,
          facilities: facilitiesLabels,
        },
      };

      await patchProjectKpis(dealId, kpiPatch);
    } catch (e) {
      console.warn("[PropertyDetails] Facilities KPI patch failed:", e);
      toast.error("Failed to save facilities update.");
      return;
    }

    setFacilitiesOpen(false);
    onSaved?.();
  };

  const getRevenueConflicts = useCallback(() => {
    if (!deal) return {};
    const conflicts: { restaurant?: boolean; bar?: boolean; spa?: boolean } =
      {};

    const currentFacilityIds = getCanonicalIds();
    const hasRestaurant = currentFacilityIds.includes("restaurant");
    const hasBar = currentFacilityIds.includes("bar");
    const hasSpa = currentFacilityIds.includes("spa");

    const fnbRevenue = deal.kpis?.fnbRevenue;
    if (fnbRevenue) {
      if (fnbRevenue.mode === "advanced" && fnbRevenue.advanced) {
        const hasRestaurantRevenue =
          (fnbRevenue.advanced.breakfast?.guestCapturePct || 0) > 0 ||
          (fnbRevenue.advanced.breakfast?.externalCoversPerDay || 0) > 0 ||
          (fnbRevenue.advanced.lunch?.guestCapturePct || 0) > 0 ||
          (fnbRevenue.advanced.lunch?.externalCoversPerDay || 0) > 0 ||
          (fnbRevenue.advanced.dinner?.guestCapturePct || 0) > 0 ||
          (fnbRevenue.advanced.dinner?.externalCoversPerDay || 0) > 0;

        const hasBarRevenue =
          (fnbRevenue.advanced.bar?.guestCapturePct || 0) > 0 ||
          (fnbRevenue.advanced.bar?.externalCoversPerDay || 0) > 0;

        if (hasRestaurantRevenue && !hasRestaurant) conflicts.restaurant = true;
        if (hasBarRevenue && !hasBar) conflicts.bar = true;
      } else if (fnbRevenue.simple) {
        if (
          ((fnbRevenue.simple.totalGuestCapturePct || 0) > 0 ||
            (fnbRevenue.simple.externalCoversPerDay || 0) > 0) &&
          !hasRestaurant
        ) {
          conflicts.restaurant = true;
        }
      }
    }

    const otherRevenue = deal.kpis?.otherRevenue || deal.kpis?.otherRev;
    if (otherRevenue) {
      const spaRev = otherRevenue.spa || otherRevenue.wellness;
      if (spaRev) {
        const hasSpaRevenue =
          (spaRev.treatmentsPerDay || 0) > 0 ||
          (spaRev.avgPricePerTreatment || spaRev.avgPrice || 0) > 0;
        if (hasSpaRevenue && !hasSpa) conflicts.spa = true;
      }
    }

    return conflicts;
  }, [deal, getCanonicalIds]);

  const canonicalIdsForDisplay = getCanonicalIds();
  const facilitiesArray = normalizeFacilities(canonicalIdsForDisplay);

  const getRoomsFromDeal = useCallback(() => {
    if (!deal) return [];

    if (
      Array.isArray(deal.normalizedRooms) &&
      deal.normalizedRooms.length > 0
    ) {
      return deal.normalizedRooms;
    }

    const kpis = deal.kpis;
    if (kpis?.roomMix && typeof kpis.roomMix === "object") {
      if (
        "types" in kpis.roomMix &&
        Array.isArray(kpis.roomMix.types) &&
        kpis.roomMix.types.length > 0
      ) {
        return kpis.roomMix.types.map((rt: any) => ({
          id: rt.id || crypto.randomUUID(),
          name: rt.name,
          count: rt.rooms,
          sqm: rt.sqm,
          adrBase: rt.adrBase,
        }));
      }
      if (Array.isArray(kpis.roomMix) && kpis.roomMix.length > 0) {
        return kpis.roomMix.map((rt: any) => ({
          id: rt.id || crypto.randomUUID(),
          name: rt.name || rt.type,
          count: rt.count || rt.rooms,
          sqm: rt.sqm,
          adrBase: rt.adrBase,
        }));
      }
    }

    if (Array.isArray(deal.rooms) && deal.rooms.length > 0) {
      return (deal as any).rooms.map((r: any) => ({
        id: r.id || crypto.randomUUID(),
        name: r.name || r.type,
        count: r.count,
        sqm: r.sqm,
        adrBase: r.adrBase,
      }));
    }

    if (Array.isArray(deal.roomTypes) && deal.roomTypes.length > 0) {
      return deal.roomTypes.map((rt: any) => ({
        id: rt.id || crypto.randomUUID(),
        name: rt.name,
        count: rt.rooms,
        sqm: rt.sqm,
        adrBase: rt.adrBase,
      }));
    }

    return [];
  }, [deal]);

  const normalizedRooms = getRoomsFromDeal();
  const legacyRoomTypes = deal?.roomTypes || [];

  const totalRoomsCount = useMemo(() => {
    if (!deal) return 0;

    const kpisRooms = deal.kpis?.rooms?.total;
    if (typeof kpisRooms === "number" && kpisRooms > 0) {
      return kpisRooms;
    }

    if (
      deal.kpis?.roomMix &&
      typeof deal.kpis.roomMix === "object" &&
      "total" in deal.kpis.roomMix
    ) {
      return deal.kpis.roomMix.total;
    }

    return normalizedRooms.length > 0
      ? computeTotalRooms(normalizedRooms)
      : totalRooms(legacyRoomTypes);
  }, [deal, normalizedRooms, legacyRoomTypes]);

  const activeServicesCount = (deal?.kpis?.facilities || []).length;

  if (!deal) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">your data is loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-1xl mx-auto">
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-6">
            <div className="flex-1">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground">
                {name || "No Name"}
              </h1>
              <p className="text-muted-foreground flex items-center gap-2 mt-2">
                <MapPin className="h-4 w-4 flex-shrink-0" />
                <span>
                  {location || "No Selected"} · {propertyType}
                </span>
              </p>
            </div>
            <div className="flex gap-6">
              <div className="text-center">
                <p className="text-2xl font-bold text-foreground">
                  {totalRoomsCount}
                </p>
                <p className="text-sm text-muted-foreground">Rooms</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-foreground">
                  {stars || 0}★
                </p>
                <p className="text-sm text-muted-foreground">Rating</p>
              </div>
            </div>
          </div>
        </div>
        <Card className="mb-8">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Property Photos</CardTitle>
              {photoUrl && (
                <span className="text-xs text-muted-foreground italic">
                  Including main URL photo
                </span>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <PhotoGallery
              photos={photos}
              mainPhotoUrl={photoUrl}
              onPhotosChange={setPhotos}
              dealId={dealId}
            />
          </CardContent>
        </Card>

        <Card className="mb-8">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Investment Thesis
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <Label className="text-sm text-muted-foreground">
                Describe the deal opportunity, key value drivers, and investment
                rationale
              </Label>
              <Textarea
                placeholder="This 25-room boutique hotel in the heart of Rome represents a unique opportunity to acquire a well-positioned asset in one of Europe's most resilient hospitality markets..."
                value={investmentThesis}
                onChange={(e) => setInvestmentThesis(e.target.value)}
                rows={4}
                className="resize-none min-h-[120px]"
              />
            </div>
          </CardContent>
        </Card>

        <Card className="mb-8">
          <CardHeader className="py-4">
            <CardTitle className="text-base flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Property Details
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Update the basic information about your hotel property and
              investment.
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">
                  Deal Name *
                </Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="My Hotel Analysis"
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">
                  Location *
                </Label>
                <Select
                  value={location}
                  onValueChange={(countryName) => setLocation(countryName)}
                >
                  <SelectTrigger
                    className={`w-full ${errors.location ? "border-red-500" : ""}`}
                  >
                    <SelectValue placeholder="Select a country" />
                  </SelectTrigger>
                  <SelectContent>
                    {countries.map((country) => (
                      <SelectItem key={country.code} value={country.name}>
                        {country.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.location && (
                  <p className="mt-1 text-sm text-red-600">{errors.location}</p>
                )}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">
                Full Address
              </Label>
              <Input
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="Street address, city, country"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">
                  Property Type *
                </Label>
                <Select
                  value={propertyType}
                  onValueChange={(value) => setPropertyType(value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {propertyTypes.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.propertyType && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.propertyType}
                  </p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">
                  Currency *
                </Label>
                <Select
                  value={currency}
                  onValueChange={(value) => setCurrency(value as CurrencyCode)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select currency" />
                  </SelectTrigger>
                  <SelectContent>
                    {currencies.map((curr) => (
                      <SelectItem key={curr.code} value={curr.code}>
                        {curr.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.currency && (
                  <p className="mt-1 text-sm text-red-600">{errors.currency}</p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">
                  Building Gross Floor Area (sqm) *
                </Label>
                <Input
                  type="number"
                  value={gfaSqm || ""}
                  onChange={(e) => setGfaSqm(Number(e.target.value) || 0)}
                  placeholder="Enter GFA"
                />
                {errors.gfaSqm && (
                  <p className="mt-1 text-sm text-red-600">{errors.gfaSqm}</p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">
                  Star Rating *
                </Label>
                <Select
                  value={stars.toString()}
                  onValueChange={(value) => setStars(Number(value))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select rating" />
                  </SelectTrigger>
                  <SelectContent>
                    {[0, 1, 2, 3, 4, 5].map((star) => (
                      <SelectItem key={star} value={star.toString()}>
                        {star === 0
                          ? "No rating"
                          : `${star} Star${star !== 1 ? "s" : ""}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.stars && (
                  <p className="mt-1 text-sm text-red-600">{errors.stars}</p>
                )}
              </div>
            </div>

            <div className="pt-4 border-t border-border">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <Label className="text-xs text-muted-foreground">
                    Total Rooms
                  </Label>
                  <p className="text-xl font-bold text-foreground">
                    {totalRoomsCount} rooms
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {normalizedRooms.length > 0
                      ? normalizedRooms
                          .map((r) => `${r.count} ${r.name}`)
                          .join(" · ")
                      : "No room types configured"}
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setRoomMixOpen(true)}
                >
                  <Edit className="h-4 w-4 mr-1" />
                  Edit room mix
                </Button>
              </div>
            </div>

            <div className="pt-4 border-t border-border">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <Label className="text-xs text-muted-foreground">
                    Facilities
                  </Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {facilitiesArray.length > 0 ? (
                      facilitiesArray.map((id) => (
                        <Badge key={id} variant="secondary">
                          {facilityLabel(id)}
                        </Badge>
                      ))
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        No facilities selected
                      </p>
                    )}
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setFacilitiesOpen(true)}
                >
                  <Edit className="h-4 w-4 mr-1" />
                  Edit Facilities
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="mb-8">
          <NotesLog notes={notes} onNotesChange={setNotes} dealId={dealId} />
        </div>

        <Card className="mb-8">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <BedDouble className="h-5 w-5" />
              Quick Stats
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-muted/30 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-foreground mb-1">
                  {totalRoomsCount}
                </p>
                <p className="text-sm text-muted-foreground">Rooms</p>
              </div>
              <div className="bg-muted/30 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-foreground mb-1">
                  {gfaSqm?.toLocaleString() || "0"}
                </p>
                <p className="text-sm text-muted-foreground">GFA (sqm)</p>
              </div>
              <div className="bg-muted/30 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-foreground mb-1">
                  {currency || "USD"}{" "}
                  {((purchasePrice || 0) / 1000000).toFixed(1)}M
                </p>
                <p className="text-sm text-muted-foreground">Purchase Price</p>
              </div>
              <div className="bg-muted/30 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-foreground mb-1">
                  {activeServicesCount}
                </p>
                <p className="text-sm text-muted-foreground">Facilities</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="pt-4 border-t">
          <div className="flex items-center space-x-3 mb-2 justify-end">
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={saveState === "saving"}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saveState === "saving"}
              className={`${
                saveState === "success"
                  ? "bg-green-600 hover:bg-green-700"
                  : "bg-[hsl(215_85%_45%)] hover:bg-[hsl(215_85%_40%)]"
              } text-white flex items-center space-x-2`}
            >
              {saveState === "saving" && (
                <Loader2 className="h-4 w-4 animate-spin" />
              )}
              {saveState === "success" && <Check className="h-4 w-4" />}
              <span>
                {saveState === "saving" && "Saving..."}
                {saveState === "success" && "Saved"}
                {(saveState === "idle" || saveState === "error") &&
                  "Save changes"}
              </span>
            </Button>
          </div>

          {saveState === "error" && saveError && (
            <div className="flex items-center space-x-2 text-red-600 text-sm font-medium animate-in fade-in-0 duration-300">
              <AlertCircle className="h-4 w-4" />
              <span>{saveError}</span>
            </div>
          )}
        </div>
      </div>

      <RoomMixModal
        dealId={dealId}
        isOpen={roomMixOpen}
        onClose={() => setRoomMixOpen(false)}
        onSaved={handleRoomMixSaved}
      />

      <FacilitiesModal
        deal={deal}
        isOpen={facilitiesOpen}
        onClose={() => setFacilitiesOpen(false)}
        facilitiesArray={canonicalIdsForDisplay as string[]}
        onSaveArray={handleFacilitiesArraySaved}
        hasRevenueConflicts={getRevenueConflicts()}
      />
    </div>
  );
}
